import { Routes, Route, Navigate } from 'react-router-dom'
import { useState, useEffect, useCallback, useRef } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'
import { ApiRequestError, getCurrentUser, ensureWorkspace, getImpersonationStatus } from './lib/api'
import Layout from './components/Layout'
import Home from './pages/Home'
import Workspace from './pages/Workspace'
import MyApps from './pages/MyApps'
import AppDetail from './pages/AppDetail'
import Settings from './pages/Settings'
import Changelog from './pages/Changelog'
import ChangelogDetail from './pages/ChangelogDetail'
import Guide from './pages/Guide'
import AdminUsers from './pages/admin/Users'
import AdminUserDetail from './pages/admin/UserDetail'
import AdminRoles from './pages/admin/Roles'
import AdminOverview from './pages/admin/Overview'
import AdminActivity from './pages/admin/Activity'
import AdminApps from './pages/admin/Apps'
import AdminAppDetail from './pages/admin/AppDetail'
import AdminWorkspaces from './pages/admin/Workspaces'
import AdminPlatformSettings from './pages/admin/PlatformSettings'
import AdminResourceLimits from './pages/admin/ResourceLimits'
import AdminClaudeCode from './pages/admin/ClaudeCode'
import AdminTemplates from './pages/admin/Templates'
import AdminTemplateAnalytics from './pages/admin/TemplateAnalytics'
import AdminSecretFindings from './pages/admin/SecretFindings'
import AdminTestNotifications from './pages/admin/TestNotifications'
import AnalyticsOverview from './pages/admin/analytics/AnalyticsOverview'
import UserAnalytics from './pages/admin/analytics/UserAnalytics'
import AppAnalytics from './pages/admin/analytics/AppAnalytics'
import AnalyticsActivityFeed from './pages/admin/analytics/ActivityFeed'
import FeatureAdoption from './pages/admin/analytics/FeatureAdoption'
import SystemObservability from './pages/admin/analytics/SystemObservability'
import ChatbotAnalytics from './pages/admin/analytics/ChatbotAnalytics'
import ChangelogVisits from './pages/admin/analytics/ChangelogVisits'
import CreatorDashboard from './pages/CreatorDashboard'
import BrowseApps from './pages/BrowseApps'
import Feedback from './pages/Feedback'
import Notifications from './pages/Notifications'
import { UserContext, UserContextType } from './hooks/useUser'
import { ChatStateProvider } from './hooks/useChatState'
import { SettingsProvider } from './hooks/useSettings'
import { WhatsNewProvider } from './hooks/useWhatsNew'
import { NotificationCountProvider } from './hooks/useNotificationCount'
import { useThemeEffect } from './hooks/useThemeEffect'
import { recordClientTelemetry } from './lib/clientTelemetry'
import { PushPromptDialog } from './components/PushPromptDialog'

// Component to apply theme effect (must be inside SettingsProvider)
function ThemeApplicator() {
  useThemeEffect()
  return null
}

interface BootstrapErrorState {
  message: string
  retryAfterSeconds: number | null
}

function classifyBootstrapError(error: unknown): BootstrapErrorState {
  if (error instanceof ApiRequestError) {
    return {
      message: error.message,
      retryAfterSeconds: error.status === 429 ? (error.retryAfterSeconds ?? 15) : null,
    }
  }

  if (error instanceof Error) {
    return {
      message: error.message,
      retryAfterSeconds: /rate limit/i.test(error.message) ? 15 : null,
    }
  }

  return {
    message: 'Failed to load Code Concierge.',
    retryAfterSeconds: null,
  }
}

function isBootstrapAuthError(error: unknown): boolean {
  return error instanceof ApiRequestError && (error.status === 401 || error.status === 403)
}

function App() {
  const [user, setUser] = useState<UserContextType | null>(null)
  const [loading, setLoading] = useState(true)
  const [bootstrapError, setBootstrapError] = useState<BootstrapErrorState | null>(null)
  const [retryCountdown, setRetryCountdown] = useState<number | null>(null)
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current !== null) {
      clearTimeout(retryTimerRef.current)
      retryTimerRef.current = null
    }
  }, [])

  const bootstrapApp = useCallback(async () => {
    clearRetryTimer()
    setLoading(true)
    setBootstrapError(null)
    setRetryCountdown(null)

    try {
      const [userData, impersonationData] = await Promise.all([
        getCurrentUser(),
        getImpersonationStatus(),
      ])

      setUser({
        email: userData.email,
        name: userData.name,
        firstName: userData.first_name,
        lastName: userData.last_name,
        services: userData.services,
        isAdmin: userData.is_admin,
        impersonation: {
          impersonating: impersonationData.impersonating,
          originalAdmin: impersonationData.original_admin,
          targetUser: impersonationData.target_user,
        },
        features: {
          templatesVisibleToUsers: userData.features?.templates_visible_to_users ?? false,
          // Resolve on the server when possible; fall back to a local is_admin || global
          // compute for brief frontend-new/backend-old windows during rollout.
          templatesVisibleToMe:
            userData.features?.templates_visible_to_me !== undefined
              ? userData.features.templates_visible_to_me
              : Boolean(userData.is_admin) || Boolean(userData.features?.templates_visible_to_users),
        },
      })

      try {
        await ensureWorkspace()
      } catch (err) {
        console.error('Failed to ensure workspace:', err)
      }
    } catch (err) {
      console.error('Failed to bootstrap dashboard:', err)

      if (isBootstrapAuthError(err)) {
        clearRetryTimer()
        setBootstrapError(null)
        setRetryCountdown(null)
        setUser(null)
        setLoading(false)
        window.location.href = '/login'
        return
      }

      setUser(null)
      const nextError = classifyBootstrapError(err)
      setBootstrapError(nextError)
      setRetryCountdown(nextError.retryAfterSeconds)
    } finally {
      setLoading(false)
    }
  }, [clearRetryTimer])

  useEffect(() => {
    void bootstrapApp()

    return () => {
      clearRetryTimer()
    }
  }, [bootstrapApp, clearRetryTimer])

  useEffect(() => {
    if (retryCountdown === null || retryCountdown < 0) {
      return
    }

    if (retryCountdown === 0) {
      void bootstrapApp()
      return
    }

    retryTimerRef.current = setTimeout(() => {
      setRetryCountdown((current) => (current === null ? null : current - 1))
    }, 1000)

    return () => {
      clearRetryTimer()
    }
  }, [bootstrapApp, clearRetryTimer, retryCountdown])

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      recordClientTelemetry({
        eventType: 'window_error',
        panel: 'dashboard',
        severity: 'error',
        message: event.message || 'Unhandled browser error',
        details: {
          filename: event.filename,
          line: event.lineno,
          column: event.colno,
        },
      })
    }

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      recordClientTelemetry({
        eventType: 'window_unhandled_rejection',
        panel: 'dashboard',
        severity: 'error',
        message: String(event.reason || 'Unhandled promise rejection'),
      })
    }

    window.addEventListener('error', handleError)
    window.addEventListener('unhandledrejection', handleUnhandledRejection)
    return () => {
      window.removeEventListener('error', handleError)
      window.removeEventListener('unhandledrejection', handleUnhandledRejection)
    }
  }, [])

  if (loading && !user && !bootstrapError) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!user && bootstrapError) {
    return (
      <div className="min-h-screen bg-background px-6 py-10 text-foreground">
        <div className="mx-auto flex min-h-[70vh] max-w-lg items-center justify-center">
          <div className="w-full rounded-2xl border border-border bg-card p-6 shadow-sm">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-amber-500/10 p-2 text-amber-600 dark:text-amber-300">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div>
                <h1 className="text-xl font-semibold">
                  {bootstrapError.retryAfterSeconds !== null
                    ? 'Rate limited while loading Code Concierge'
                    : 'Unable to load Code Concierge'}
                </h1>
                <p className="mt-2 text-sm text-muted-foreground">{bootstrapError.message}</p>
                {retryCountdown !== null && (
                  <p className="mt-2 text-sm text-muted-foreground">
                    Retrying automatically in about {Math.max(0, retryCountdown)}s.
                  </p>
                )}
              </div>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => void bootstrapApp()}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                {loading ? 'Retrying...' : 'Retry now'}
              </button>
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium transition-colors hover:bg-secondary"
              >
                Reload page
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!user) {
    return null
  }

  return (
    <UserContext.Provider value={user}>
      <SettingsProvider>
        <NotificationCountProvider>
        <ChatStateProvider>
          <WhatsNewProvider>
            <ThemeApplicator />
            <PushPromptDialog />
            <Routes>
              {/* Full-screen workspace page (no Layout) */}
              <Route path="/workspace" element={<Workspace />} />

              {/* Standard pages with sidebar Layout */}
              <Route path="/*" element={
                <Layout>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/apps" element={<MyApps />} />
                    <Route path="/apps/:appName" element={<AppDetail />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/changelog" element={<Changelog />} />
                    <Route path="/changelog/:slug" element={<ChangelogDetail />} />
                    <Route path="/guide" element={<Guide />} />
                    <Route path="/guide/:section" element={<Guide />} />
                    <Route path="/browse" element={<BrowseApps />} />
                    <Route path="/creator-dashboard" element={<CreatorDashboard />} />
                    <Route path="/feedback" element={<Feedback />} />
                    <Route path="/notifications" element={<Notifications />} />
                    {user.isAdmin && (
                      <>
                        <Route path="/admin" element={<AdminOverview />} />
                        <Route path="/admin/users" element={<AdminUsers />} />
                        <Route path="/admin/users/:userId" element={<AdminUserDetail />} />
                        <Route path="/admin/roles" element={<AdminRoles />} />
                        <Route path="/admin/workspaces" element={<AdminWorkspaces />} />
                        <Route path="/admin/apps" element={<AdminApps />} />
                        <Route path="/admin/apps/:appName" element={<AdminAppDetail />} />
                        <Route path="/admin/activity" element={<AdminActivity />} />
                        <Route path="/admin/secret-findings" element={<AdminSecretFindings />} />
                        <Route path="/admin/test-notifications" element={<AdminTestNotifications />} />
                        <Route path="/admin/platform-settings" element={<AdminPlatformSettings />} />
                        <Route path="/admin/templates" element={<AdminTemplates />} />
                        <Route path="/admin/resource-limits" element={<AdminResourceLimits />} />
                        <Route path="/admin/claude-code" element={<AdminClaudeCode />} />
                        <Route path="/admin/analytics" element={<AnalyticsOverview />} />
                        <Route path="/admin/analytics/users" element={<UserAnalytics />} />
                        <Route path="/admin/analytics/apps" element={<AppAnalytics />} />
                        <Route path="/admin/analytics/activity" element={<AnalyticsActivityFeed />} />
                        <Route path="/admin/analytics/features" element={<FeatureAdoption />} />
                        <Route path="/admin/analytics/system" element={<SystemObservability />} />
                        <Route path="/admin/analytics/chatbot" element={<ChatbotAnalytics />} />
                        <Route path="/admin/analytics/templates" element={<AdminTemplateAnalytics />} />
                        <Route path="/admin/analytics/changelog" element={<ChangelogVisits />} />
                      </>
                    )}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Layout>
              } />
            </Routes>
          </WhatsNewProvider>
        </ChatStateProvider>
        </NotificationCountProvider>
      </SettingsProvider>
    </UserContext.Provider>
  )
}

export default App
// Dashboard rebuild trigger
